/**
 * ⚠️ HAND-WRITTEN FILE - DO NOT DELETE! ⚠️
 *
 * Hideable Cover Flow key: arrow keys, wheel/trackpad, and Enter.
 */

import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import { analytics } from '@/lib/analytics';

const STORAGE_KEY = 'finder-carousel-key';

function Keycap({ children }: { children: string }) {
  return (
    <Box
      component="kbd"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 22,
        height: 20,
        px: 0.5,
        borderRadius: '4px',
        border: '1px solid rgba(255,255,255,0.22)',
        bgcolor: 'rgba(255,255,255,0.08)',
        fontSize: '0.7rem',
        fontFamily: 'inherit',
        lineHeight: 1,
        color: 'white',
      }}
    >
      {children}
    </Box>
  );
}

function Hint({ keys, label }: { keys: string[]; label: string }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      {keys.map((key) => (
        <Keycap key={key}>{key}</Keycap>
      ))}
      <Typography
        variant="caption"
        sx={{ opacity: 0.78, whiteSpace: 'nowrap', pl: 0.25, display: { xs: 'none', md: 'inline' } }}
      >
        {label}
      </Typography>
    </Stack>
  );
}

export function HomeFinderKey({ showScroll }: { showScroll: boolean }) {
  const [visible, setVisible] = useState<boolean | null>(null);

  useEffect(() => {
    setVisible(localStorage.getItem(STORAGE_KEY) !== 'hidden');
  }, []);

  const hide = () => {
    localStorage.setItem(STORAGE_KEY, 'hidden');
    setVisible(false);
    analytics.homeFinderKeyToggled(false);
  };

  const show = () => {
    localStorage.removeItem(STORAGE_KEY);
    setVisible(true);
    analytics.homeFinderKeyToggled(true);
  };

  if (visible === null) return null;

  if (!visible) {
    return (
      <IconButton
        size="small"
        aria-label="Show carousel key"
        onClick={show}
        sx={{
          color: 'white',
          opacity: 0.75,
          p: 0.5,
          '&:hover': { opacity: 1, bgcolor: 'rgba(255,255,255,0.08)' },
        }}
      >
        <KeyboardIcon sx={{ fontSize: 18 }} />
      </IconButton>
    );
  }

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={{ xs: 0.75, md: 1.25 }}
      aria-label="Cover Flow key"
      sx={{
        px: 0.75,
        py: 0.25,
        borderRadius: '8px',
        bgcolor: 'rgba(255,255,255,0.06)',
        border: '1px solid rgba(255,255,255,0.1)',
      }}
    >
      <Hint keys={['←', '→']} label="browse" />
      {showScroll && <Hint keys={['↑', '↓']} label="scroll" />}
      <Hint keys={['⏎']} label="open" />
      <IconButton
        size="small"
        aria-label="Hide carousel key"
        onClick={hide}
        sx={{ color: 'white', opacity: 0.7, p: 0.25, '&:hover': { opacity: 1 } }}
      >
        <CloseIcon sx={{ fontSize: 14 }} />
      </IconButton>
    </Stack>
  );
}
